/**
 * Bunny Upload Service
 *
 * Client-side orchestrator for uploading assets to Bunny Storage + Stream.
 * Called unconditionally from clientAssetsStore — there is no longer a
 * per-company storageBackend branch; every upload goes to Bunny. (Transfers
 * are the one exception and never route through here; they upload straight to
 * Firebase Storage via transferService.)
 *
 * Video flow:  createBunnyStreamUpload callable → TUS direct upload to Bunny
 *              Stream. Stream keeps the original file automatically.
 * Non-video:   HTTP POST to /api/bunny-upload which proxies bytes to
 *              Bunny Storage server-side (Bunny has no per-file presigned PUT).
 *
 * Returns Bunny-specific pointer fields; the caller merges with standard
 * asset metadata and writes the Firestore doc.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { collection, doc, setDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytesResumable } from 'firebase/storage';
import * as tus from 'tus-js-client';
import { db, storage } from '@/js/firebase';
import { canonicalAssetName, detectAssetType } from './assetUploadService';
import { enqueueTranscription } from './transcriptionService';
import { logger } from './loggerService';
import { BUNNY_URL_INTENT, BUNNY_UPLOAD_CONTEXT } from '@/utils/bunnyConstants';

const BUNNY_UPLOAD_ENDPOINT = '/api/bunny-upload';

async function getIdToken() {
  const user = getAuth().currentUser;
  if (!user) throw new Error('Not signed in');
  return user.getIdToken();
}

/**
 * Upload a file to Bunny. Branches internally on video vs non-video.
 *
 * @param {Object} params
 * @param {File} params.file - Browser File object
 * @param {string} params.companyId
 * @param {string} [params.assetId] - Legacy shorthand; equivalent to passing
 *   context: { type: 'asset', assetId }. Either assetId or context is required.
 * @param {Object} [params.context] - Routing context for the upload.
 *   Either { type: 'asset', assetId } or
 *   { type: 'review_version', reviewId, itemId, versionId }.
 * @param {(progress: number) => void} [params.onProgress]
 * @param {(task: { pause: () => void, resume: () => void }) => void} [params.onTask]
 *   Invoked once the resumable (TUS video) upload handle exists, so callers can
 *   wire pause/resume + stall-recovery. Only fired for the video path; non-video
 *   uploads are small XHR POSTs with no resumable handle.
 * @returns {Promise<{
 *   kind: 'video' | 'non-video',
 *   bunnyStream?: { libraryId, videoId, status },
 *   bunnyStorage?: { path, cdnHostname, size, contentType }
 * }>}
 */
export async function uploadAsset({ file, companyId, assetId, context, onProgress, onTask }) {
  if (!file) throw new Error('file is required');
  if (!companyId) throw new Error('companyId is required');

  // Normalize legacy callers that pass top-level assetId.
  const ctx = context || (assetId ? { type: 'asset', assetId } : null);
  if (!ctx) throw new Error('context or assetId is required');

  // Route by video-ness. Trust an explicit video MIME, but otherwise defer to
  // detectAssetType, which also recognizes video by extension — so empty-MIME
  // or octet-stream containers (.mkv, some .mov) still go to Stream instead of
  // landing in Storage as an unplayable, un-transcoded blob.
  const mime = (file.type || '').toLowerCase();
  const isVideo = mime.startsWith('video/') || ['videos', 'selects'].includes(detectAssetType(file));
  if (isVideo) {
    return uploadVideo({ file, companyId, context: ctx, onProgress, onTask });
  }
  return uploadNonVideo({ file, companyId, context: ctx, onProgress });
}

async function uploadVideo({ file, companyId, context, onProgress, onTask }) {
  const functions = getFunctions();
  const call = httpsCallable(functions, 'createBunnyStreamUpload');

  let creds;
  try {
    const payload = { companyId, context, filename: file.name };
    // Top-level assetId is a compat shim for pre-context createBunnyStreamUpload deployments; remove once all envs are on the new function.
    if (context.type === BUNNY_UPLOAD_CONTEXT.ASSET) payload.assetId = context.assetId;
    const { data } = await call(payload);
    creds = data;
  } catch (err) {
    logger.error('[BunnyUpload] createBunnyStreamUpload call failed', { error: err.message });
    throw new Error(`Failed to start Bunny Stream upload: ${err.message}`);
  }

  if (!creds?.videoId || !creds?.tusEndpoint) {
    throw new Error('Upload service did not return valid credentials');
  }

  // Chunked so progress can count what Bunny has ACKNOWLEDGED, not just what
  // the browser has sent. Unchunked, "100% sent" was followed by a silent wait
  // for Bunny's ack of the whole file — long enough on big videos that the
  // stall watchdog aborted it and restarted (the "stuck at 100%" reports).
  const CHUNK_BYTES = 50 * 1024 * 1024;
  let bytesSent = 0;
  let bytesAccepted = 0;
  // Half sent, half acknowledged: keeps moving while sending, and only reads
  // 100 once Bunny has confirmed every byte.
  const reportProgress = () => {
    if (onProgress && file.size > 0) {
      onProgress(Math.round(((bytesSent + bytesAccepted) / (2 * file.size)) * 100));
    }
  };
  // The current chunk is fully sent and its PATCH is waiting on Bunny's reply.
  const isAwaitingAck = () => bytesSent >= Math.min(bytesAccepted + CHUNK_BYTES, file.size);
  let isPaused = false;
  let pauseAfterAck = false;

  await new Promise((resolve, reject) => {
    const upload = new tus.Upload(file, {
      endpoint: creds.tusEndpoint,
      chunkSize: CHUNK_BYTES,
      // ~5 min total: rides out a network blip instead of failing every queued
      // file in turn (Sept 25 2026: a ~90s outage failed 10 files at offset 0).
      retryDelays: [0, 1000, 3000, 5000, 10000, 20000, 30000, 60000, 60000, 60000, 60000],
      headers: {
        AuthorizationSignature: creds.authorizationSignature,
        AuthorizationExpire: String(creds.authorizationExpire),
        VideoId: creds.videoId,
        LibraryId: String(creds.libraryId),
      },
      metadata: {
        filetype: file.type,
        title: creds.title || file.name,
      },
      onError: (err) => {
        logger.error('[BunnyUpload] TUS upload error', { error: err?.message || err });
        // The Stream video record already exists (0 bytes) and a retry mints a
        // fresh one, so this one would sit in the library forever. Best-effort.
        httpsCallable(functions, 'deleteBunnyArtifacts')({ companyId, videoId: creds.videoId })
          .catch((e) => logger.warn('[BunnyUpload] Failed to delete abandoned Stream video', {
            videoId: creds.videoId, error: e?.message,
          }));
        reject(err);
      },
      onProgress: (bytesUploaded) => {
        bytesSent = bytesUploaded;
        reportProgress();
      },
      onChunkComplete: (_chunkSize, accepted) => {
        bytesAccepted = accepted;
        reportProgress();
        if (pauseAfterAck && accepted < file.size) {
          pauseAfterAck = false;
          isPaused = true;
          upload.abort();
        }
      },
      onSuccess: () => resolve(),
    });
    upload.start();

    // Expose a pause/resume handle so callers (review upload queue) can wire
    // manual pause/resume + the stall-recovery watchdog. TUS resumes from the
    // last committed offset, so abort()/start() is a true pause/resume — the
    // same shape attachStallRecovery + pauseQueueItem expect (.pause/.resume).
    if (typeof onTask === 'function') {
      onTask({
        pause: () => {
          // No progress events while Bunny acks a chunk — that's not a stall,
          // and aborting it throws the chunk away. Defer to the ack instead.
          if (isAwaitingAck()) {
            pauseAfterAck = true;
            return;
          }
          isPaused = true;
          try { upload.abort(); } catch { /* already settled */ }
        },
        resume: () => {
          pauseAfterAck = false;
          if (!isPaused) return; // never actually aborted — start() would double-run it
          isPaused = false;
          try { upload.start(); } catch { /* already settled */ }
        },
      });
    }
  });

  return {
    kind: 'video',
    bunnyStream: {
      libraryId: String(creds.libraryId),
      videoId: creds.videoId,
      collectionId: creds.collectionId || null,
      cdnHostname: creds.cdnHostname || null,
      status: 'processing',
      // mp4Variants, hlsPath, thumbnailPath populated by onBunnyStreamReady webhook
      // as transcoding progresses. No direct playback URL is valid until at least
      // one resolution finishes encoding.
    },
  };
}

async function uploadNonVideo({ file, companyId, context, onProgress }) {
  const result = await postFileToBunny({
    file,
    onProgress,
    // Same routing data the headers below carry, as structured JSON — the
    // staged route posts it to a callable instead of an XHR.
    staged: { contextType: context.type, companyId, context },
    setExtraHeaders: (xhr) => {
      xhr.setRequestHeader('X-Vysual-Company-Id', companyId);
      xhr.setRequestHeader('X-Vysual-Context', context.type);
      if (context.type === BUNNY_UPLOAD_CONTEXT.ASSET) {
        xhr.setRequestHeader('X-Vysual-Asset-Id', context.assetId);
      } else if (context.type === BUNNY_UPLOAD_CONTEXT.REVIEW_VERSION) {
        xhr.setRequestHeader('X-Vysual-Review-Id', context.reviewId);
        xhr.setRequestHeader('X-Vysual-Item-Id', context.itemId);
        xhr.setRequestHeader('X-Vysual-Version-Id', context.versionId);
      }
    },
  });

  return {
    kind: 'non-video',
    bunnyStorage: {
      path: result.path,
      cdnHostname: result.cdnHostname,
      size: file.size,
      contentType: file.type || 'application/octet-stream',
    },
  };
}

/**
 * Upload a file to a non-asset Bunny location (temp attachments, user
 * profile assets). Does NOT create or expect a Firestore asset doc.
 *
 * @param {Object} params
 * @param {File|Blob} params.file
 * @param {string} [params.fileName] - Required if `file` is a Blob without a name.
 * @param {Object} params.context
 *   { type: 'temp_attachment', namespace, contextId }
 *   { type: 'user_profile', userId }
 * @param {(progress: number) => void} [params.onProgress]
 * @returns {Promise<{ path: string, cdnHostname: string }>}
 */
export async function uploadRawFile({ file, fileName, context, onProgress }) {
  if (!file) throw new Error('file is required');
  if (!context?.type) throw new Error('context.type is required');

  return postFileToBunny({
    file,
    fileName,
    onProgress,
    staged: { contextType: context.type, context },
    setExtraHeaders: (xhr) => {
      xhr.setRequestHeader('X-Vysual-Context', context.type);
      if (context.type === BUNNY_UPLOAD_CONTEXT.TEMP_ATTACHMENT) {
        if (!context.namespace || !context.contextId) {
          throw new Error('temp_attachment context requires namespace and contextId');
        }
        xhr.setRequestHeader('X-Vysual-Namespace', context.namespace);
        xhr.setRequestHeader('X-Vysual-Context-Id', context.contextId);
      } else if (context.type === BUNNY_UPLOAD_CONTEXT.USER_PROFILE) {
        if (!context.userId) {
          throw new Error('user_profile context requires userId');
        }
        xhr.setRequestHeader('X-Vysual-User-Id', context.userId);
      } else {
        throw new Error(`uploadRawFile: unsupported context type ${context.type}`);
      }
    },
  });
}

/**
 * Upload a company-level document (contract PDFs). Admin-only, enforced
 * server-side on both routes.
 *
 * Contracts deliberately create no `assets` doc — the caller stores the
 * returned `path` and re-signs it on read — so they can't ride the `asset`
 * context, which builds its path around an assetId. Hence their own context.
 *
 * @returns {Promise<{ path: string, cdnHostname: string }>}
 */
export async function uploadCompanyDocument({ file, fileName, companyId, onProgress }) {
  if (!file) throw new Error('file is required');
  if (!companyId) throw new Error('companyId is required');

  return postFileToBunny({
    file,
    fileName,
    onProgress,
    staged: { contextType: BUNNY_UPLOAD_CONTEXT.COMPANY_DOCUMENT, companyId, context: {} },
    setExtraHeaders: (xhr) => {
      xhr.setRequestHeader('X-Vysual-Context', BUNNY_UPLOAD_CONTEXT.COMPANY_DOCUMENT);
      xhr.setRequestHeader('X-Vysual-Company-Id', companyId);
    },
  });
}

// Cloud Functions v2 (Cloud Run) hard-caps HTTP request bodies at 32 MiB. An
// oversized POST is rejected by the frontend before it reaches the container:
// no Cloud Run log, and the browser gets a bare "500 Internal Error" with no
// JSON body — indistinguishable from a real server fault. So anything over the
// cap takes the staged two-hop route instead (see uploadViaStaging). Videos
// skip this path entirely — they go direct-to-Stream via TUS, no cap.
const BUNNY_PROXY_MAX_BYTES = 32 * 1024 * 1024;

/**
 * The >32 MiB route: browser → Firebase Storage `_staging/uploads/{uid}/…`
 * (direct to GCS, resumable, no proxy and no size cap), then the
 * finalizeStagedBunnyUpload callable streams that object into Bunny Storage and
 * deletes the staging copy. Generalizes the two-hop path contracts have used
 * since the same 32 MiB wall hit large scanned MSAs.
 *
 * Bunny Edge Storage has no presigned URL, per-file token, TUS endpoint or
 * fetch-from-URL, so the browser can never write to Bunny directly — a staging
 * hop is the only route that avoids the proxy.
 *
 * Returns the same `{ path, cdnHostname, url? }` shape as the proxy so callers
 * can't tell which route a file took.
 */
async function uploadViaStaging({ file, fileName, contextType, context, companyId, onProgress }) {
  const uid = getAuth().currentUser?.uid;
  if (!uid) throw new Error('Not signed in');

  // Path shape is load-bearing twice over: `_staging/` is what the bucket
  // lifecycle rule and the asset audit classifier key off, and the {uid}
  // segment is what both Storage rules and the callable check to stop one user
  // finalizing another's staged object.
  const safeName = fileName.replace(/[^\w.-]+/g, '_');
  const stagingPath = `_staging/uploads/${uid}/${Date.now()}_${safeName}`;

  const task = uploadBytesResumable(storageRef(storage, stagingPath), file, {
    contentType: file.type || 'application/octet-stream',
  });

  await new Promise((resolve, reject) => {
    task.on(
      'state_changed',
      (snap) => {
        // Cap at 90 — the handoff still has to run, and a bar that sits at 100%
        // through a multi-second server-side stream reads as a hang.
        if (onProgress && snap.totalBytes > 0) {
          onProgress(Math.round((snap.bytesTransferred / snap.totalBytes) * 90));
        }
      },
      reject,
      resolve
    );
  });

  // The function itself is allowed 540s to stream the staged object into Bunny;
  // without this the SDK's 70s default aborts the call and reports deadline-exceeded
  // while the server is still working.
  const fn = httpsCallable(getFunctions(), 'finalizeStagedBunnyUpload', { timeout: 540000 });
  const { data } = await fn({ stagingPath, filename: fileName, contextType, context, companyId });

  if (onProgress) onProgress(100);
  logger.info('[BunnyUpload] Large file staged through Firebase Storage', {
    fileName, size: file.size, path: data?.path,
  });
  return data;
}

async function postFileToBunny({ file, fileName, onProgress, setExtraHeaders, staged }) {
  const effectiveName = fileName || file.name || 'file';

  if (file.size > BUNNY_PROXY_MAX_BYTES) {
    if (!staged) {
      const mib = (file.size / (1024 * 1024)).toFixed(1);
      throw new Error(
        `${effectiveName} is ${mib} MB — this upload type is limited to 32 MB.`
      );
    }
    return uploadViaStaging({ file, fileName: effectiveName, onProgress, ...staged });
  }

  const idToken = await getIdToken();

  const attempt = () => new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', BUNNY_UPLOAD_ENDPOINT, true);
    xhr.setRequestHeader('Authorization', `Bearer ${idToken}`);
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    xhr.setRequestHeader('X-Vysual-Filename', encodeURIComponent(effectiveName));
    try {
      setExtraHeaders(xhr);
    } catch (err) {
      reject(err);
      return;
    }

    xhr.upload.onprogress = (evt) => {
      if (onProgress && evt.lengthComputable && evt.total > 0) {
        const pct = Math.round((evt.loaded / evt.total) * 100);
        onProgress(pct);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch {
          resolve({});
        }
        return;
      }
      reject(new Error(`Bunny upload failed: ${xhr.status} ${xhr.statusText} — ${xhr.responseText || ''}`));
    };
    xhr.onerror = () => reject(new Error('Upload network error'));
    xhr.send(file);
  });

  // ponytail: retry transient 5xx / network errors twice — 2s, then 20s. The
  // POST targets a deterministic path (overwrite-safe), so retrying is
  // idempotent.
  //
  // The long second delay is load-bearing. These failures are Firebase Hosting
  // edge drops on the /api/bunny-upload rewrite: the bodies are Google's own
  // 500/502 pages, not this function's JSON, and Cloud Run logs no request at
  // all for them. The bad edge state outlasts a couple of seconds, so a
  // 2s-only retry still left ~8% of a 90-file batch failed in prod (Aug 2026).
  // Next rung if two retries stop being enough: POST straight to the
  // function's Cloud Run URL and drop the Hosting hop entirely.
  const RETRY_DELAYS_MS = [2000, 20000];
  for (let i = 0; ; i++) {
    try {
      return await attempt();
    } catch (err) {
      const isTransient = /Bunny upload failed: 5\d\d|network error/i.test(err.message || '');
      if (!isTransient || i >= RETRY_DELAYS_MS.length) throw err;
      logger.warn('[BunnyUpload] Transient upload failure, retrying', {
        error: err.message, attempt: i + 1, delayMs: RETRY_DELAYS_MS[i],
      });
      await new Promise((r) => setTimeout(r, RETRY_DELAYS_MS[i]));
    }
  }
}

/**
 * Mint a signed read URL for a Bunny-backed asset.
 *
 * Returns null on failure (logged). Callers should treat null as "no
 * URL available right now" — typically a soft fallback rather than a
 * fatal error.
 */
export async function mintBunnyAssetUrl(assetId, intent = BUNNY_URL_INTENT.THUMBNAIL) {
  if (!assetId) return null;
  try {
    const fn = httpsCallable(getFunctions(), 'getBunnyAssetUrl');
    const { data } = await fn({ assetId, intent });
    return data?.url || null;
  } catch (err) {
    logger.warn('[BunnyUpload] mintBunnyAssetUrl failed', {
      assetId, intent, error: err.message,
    });
    return null;
  }
}

/**
 * Delete a Bunny-backed asset (Firestore doc + Bunny artifacts).
 *
 * Idempotent: missing assetIds and missing Bunny artifacts are
 * treated as success (logged + swallowed).
 */
export async function deleteBunnyAssetById(assetId) {
  if (!assetId) return;
  try {
    const fn = httpsCallable(getFunctions(), 'deleteBunnyAsset');
    await fn({ assetId });
  } catch (err) {
    logger.warn('[BunnyUpload] deleteBunnyAssetById failed', {
      assetId, error: err.message,
    });
  }
}

/**
 * Upload a file to Bunny AND create the Firestore asset doc — the
 * common library-asset flow used by company logos, contract PDFs,
 * podcast logos, episode thumbnails, brief attachments, and calendar
 * production schedules.
 *
 * The asset shape is fixed; pass any extra fields (eventId, episodeId,
 * etc) via `extraFields`. Returns `{ assetId, url, bunnyResult }`.
 *
 * For videos the `bunnyStream` pointer is written and `bunnyStorage`
 * cleared; for non-video, vice versa.
 */
export async function createLibraryAsset({
  file,
  companyId,
  companyName = '',
  assetType,
  tags = [],
  project = '',
  projectName = '',
  deliverableId = '',
  uploadedByClient = false,
  uploadedBy = '',
  uploadSource = 'admin',
  bucket = null,
  extraFields = {},
  urlIntent = BUNNY_URL_INTENT.THUMBNAIL,
}) {
  if (!file) throw new Error('file is required');
  if (!companyId) throw new Error('companyId is required');
  if (!assetType) throw new Error('assetType is required');

  const newAssetRef = doc(collection(db, 'assets'));
  const assetId = newAssetRef.id;

  const bunnyResult = await uploadAsset({ file, companyId, assetId });

  const assetObject = {
    name: canonicalAssetName(file.name),
    size: file.size,
    type: file.type,
    company: companyId,
    companyName,
    project,
    projectName,
    deliverableId,
    assetType,
    tags,
    aspectRatio: null,
    width: null,
    height: null,
    uploadDate: Date.now(),
    uploadedByClient,
    uploadedBy,
    uploadSource,
    bucket,
    storageBackend: 'bunny',
    ...extraFields,
  };

  if (bunnyResult.kind === 'video') {
    assetObject.bunnyStream = {
      libraryId: bunnyResult.bunnyStream.libraryId,
      videoId: bunnyResult.bunnyStream.videoId,
      collectionId: bunnyResult.bunnyStream.collectionId || null,
      cdnHostname: bunnyResult.bunnyStream.cdnHostname || null,
      status: bunnyResult.bunnyStream.status,
    };
  } else {
    assetObject.bunnyStorage = {
      path: bunnyResult.bunnyStorage.path,
      cdnHostname: bunnyResult.bunnyStorage.cdnHostname,
      size: bunnyResult.bunnyStorage.size,
      contentType: bunnyResult.bunnyStorage.contentType,
    };
  }

  await setDoc(newAssetRef, assetObject);

  // Audio has no Bunny Stream webhook to auto-enqueue transcription (video does),
  // so kick it off here. Best-effort — a hiccup must not fail the upload; the
  // admin backfill and manual button cover any miss. ponytail: fire-and-forget.
  if (assetType === 'audio') {
    enqueueTranscription(assetId).catch((err) =>
      logger.warn('[bunnyUploadService] audio transcription enqueue failed', { assetId, error: err?.message })
    );
  }

  const url = await mintBunnyAssetUrl(assetId, urlIntent);

  return { assetId, url, bunnyResult };
}
